import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  ShippingCarriersFlatTeamSettingGroupById,
  type ShippingCarriersFlatTeamSettingGroupId,
} from "./groups";

type ShippingCarriersSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: ShippingCarriersSettingInput,
): ShippingCarriersSettingInput {
  return metadata;
}

function shippingCarriersGroup<
  const Group extends ShippingCarriersFlatTeamSettingGroupId,
  const T extends Record<string, ShippingCarriersSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const ShippingCarriersRegistry = {
  ...shippingCarriersGroup("shipping_carriers.carriers", {
    "outboundLabels/disabledRedoServiceLevels": setting({
      label: "Redo carrier service levels",
      description:
        "Which Redo carrier service levels are enabled for purchasing outbound labels. Disabled service levels are hidden from rate selection.",
      synonyms: [
        "redo carriers",
        "redo carrier service levels",
        "disabled service levels",
        "carrier services",
      ],
    }),
    "outboundLabels/carrierAccountDisabledServiceLevels": setting({
      label: "Your carrier service levels",
      description:
        "Which service levels are enabled for each of your connected merchant carrier accounts. Disabled service levels are hidden from rate selection.",
      synonyms: [
        "your carriers",
        "merchant carrier service levels",
        "carrier account service levels",
        "carrier services",
      ],
    }),
  }),
};

type ShippingCarriersFlatTeamSettingKey = keyof typeof ShippingCarriersRegistry;
const ShippingCarriersFlatTeamSettingKeys = Object.keys(
  ShippingCarriersRegistry,
) as ShippingCarriersFlatTeamSettingKey[];

export const ShippingCarriersFlatTeamSettingCatalogDefinitions =
  ShippingCarriersFlatTeamSettingKeys.map((key) => {
    const definition = ShippingCarriersRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: ShippingCarriersFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    ShippingCarriersFlatTeamSettingKey,
    ShippingCarriersFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<ShippingCarriersFlatTeamSettingKey>
  >[];

export const ShippingCarriersFlatTeamSettingMetadataByKey = Object.fromEntries(
  ShippingCarriersFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<ShippingCarriersFlatTeamSettingKey, SettingMetadata>;
